import { WORLD_MAP_CLUSTER_MAX_ZOOM, WORLD_MAP_CLUSTER_MIN_POINTS, WORLD_MAP_CLUSTER_RADIUS_PX, WORLD_MAP_CLUSTER_TILE_EXTENT } from "@components/world-map/clustering/world-map-cluster-constants";
import Supercluster from "supercluster";

/** Geographic bounding box in the `[westLng, southLat, eastLng, northLat]` order supercluster expects. */

/** Properties carried on each indexed point so the original entity survives the round trip. */

/** A group of nearby entities collapsed into a single bubble. */

/** What the map should draw for one camera position: bubbles plus leftover single pins. */

/**
 * Narrows a supercluster result to a cluster. Individual points pass through the
 * index untouched, so the marker `cluster` flag is the only reliable signal.
 */
function isClusterFeature(feature) {
  return "cluster" in feature.properties;
}

/**
 * Builds the spatial index for a set of entities. Loading is O(n log n) and the
 * index is immutable afterwards, so it must be rebuilt when the entities change.
 */
export function createClusterIndex(entities) {
  const index = new Supercluster({
    radius: WORLD_MAP_CLUSTER_RADIUS_PX,
    extent: WORLD_MAP_CLUSTER_TILE_EXTENT,
    maxZoom: WORLD_MAP_CLUSTER_MAX_ZOOM,
    minPoints: WORLD_MAP_CLUSTER_MIN_POINTS
  });
  index.load(entities.map(entity => ({
    type: "Feature",
    properties: {
      entity
    },
    geometry: {
      type: "Point",
      coordinates: [entity.position.lng, entity.position.lat]
    }
  })));
  return index;
}

/**
 * Every entity inside a cluster, however deeply nested its sub-clusters are.
 * Returns null when the id belongs to a previous index, built for an older entity set.
 */
export function getClusterEntities(index, clusterId) {
  try {
    return index.getLeaves(clusterId, Infinity).map(leaf => leaf.properties.entity);
  } catch {
    return null;
  }
}

/**
 * Splits what supercluster returns for the given viewport into the two sets the
 * map draws on separate layers: bubbles and individual pins.
 */
export function getClusterResult(index, bbox, zoom) {
  const clusters = [];
  const markers = [];
  for (const feature of index.getClusters(bbox, zoom)) {
    const [lng, lat] = feature.geometry.coordinates;
    if (isClusterFeature(feature)) {
      clusters.push({
        id: feature.properties.cluster_id,
        count: feature.properties.point_count,
        label: String(feature.properties.point_count_abbreviated),
        lng,
        lat
      });
      continue;
    }
    markers.push(feature.properties.entity);
  }
  return {
    clusters,
    markers
  };
}